/**
 * Blossom — modal.js
 * <modal-component> — centered dialog on desktop, bottom sheet on mobile,
 * built on a native <dialog> (see Theme.Dialog in global.js).
 * Used for quick add, shade/size guides, video, newsletter popups and quizzes.
 *
 * Markup: snippets/modal.liquid
 * Open with any element: data-open="ModalId" aria-controls="ModalId"
 * Remote content: modal.loadSection({ url, sectionId, selector })
 * Events: theme:modal:open / theme:modal:close  { id }
 */
(() => {
  if (!window.Theme || !Theme.Dialog || customElements.get('modal-component')) return;

  class ModalComponent extends Theme.Dialog {
    static openEvent = Theme.events.MODAL_OPEN;
    static closeEvent = Theme.events.MODAL_CLOSE;

    get content() {
      return this.querySelector('[data-modal-content]');
    }

    /**
     * Load a section (Section Rendering API) into the modal content area with
     * loading → success | error states. Resolves with the content element.
     */
    async loadSection({ url, sectionId, selector, fallbackUrl }) {
      const content = this.content;
      if (!content) return null;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      content.setAttribute('aria-busy', 'true');
      content.innerHTML = `<div class="modal__loading"><span class="spinner" role="status" aria-label="${
        Theme.strings.loading || 'Loading'
      }"></span></div>`;

      try {
        const doc = await Theme.fetchSection(url, sectionId, { signal: this.abortController.signal });
        const source = selector ? doc.querySelector(selector) : doc.body;
        if (!source) throw new Theme.Error(Theme.strings.genericError);
        content.innerHTML = source.innerHTML;
        content.removeAttribute('aria-busy');
        this.runScripts(content);
        this.focusInitial();
        return content;
      } catch (error) {
        if (error.name === 'AbortError') return null;
        content.removeAttribute('aria-busy');
        this.renderError(error.message, fallbackUrl || url);
        return null;
      }
    }

    renderError(message, fallbackUrl) {
      const content = this.content;
      if (!content) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'modal__error';
      wrapper.setAttribute('role', 'alert');
      const text = document.createElement('p');
      text.textContent = message || Theme.strings.genericError;
      wrapper.appendChild(text);
      if (fallbackUrl) {
        const link = document.createElement('a');
        link.className = 'button button--secondary';
        link.href = fallbackUrl;
        link.textContent = Theme.strings.viewFullDetails || 'View full details';
        wrapper.appendChild(link);
      }
      content.replaceChildren(wrapper);
    }

    /** Scripts inside injected HTML don't run by default — re-create them. */
    runScripts(container) {
      container.querySelectorAll('script').forEach((oldScript) => {
        if (oldScript.type && !/javascript|module/.test(oldScript.type)) return;
        if (oldScript.src) {
          // Remove the inert injected tag first, otherwise loadScript() would find
          // it and wait for a load event that never fires.
          const src = oldScript.src;
          oldScript.remove();
          Theme.loadScript(src).catch(() => {});
          return;
        }
        const script = document.createElement('script');
        [...oldScript.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
        script.textContent = oldScript.textContent;
        oldScript.replaceWith(script);
      });
    }

    onClose() {
      // Stop any media that is playing inside the modal.
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.querySelectorAll('iframe').forEach((iframe) => {
        if (/youtube|vimeo/.test(iframe.src)) {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        }
      });
      if (this.abortController) this.abortController.abort();
      if (this.dataset.clearOnClose === 'true' && this.content) this.content.innerHTML = '';
    }
  }

  customElements.define('modal-component', ModalComponent);
})();
